import {getMarket} from "../utils/market";
import {listMarketBusinesses} from "../data/marketplaceBusinesses";
import { useEffect, useRef, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";

function normalizeText(s) {
  if (s == null) return "";
  try {
    return String(s)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  } catch {
    return String(s).toLowerCase().trim();
  }
}

/**
 * Build a location label from business fields, keeping it readable.
 * You can tune field priority here.
 */
function buildBizLocationLabel(b) {
  const zone = b.zone || b.zona || null;
  const correg = b.corregimiento || null;
  const dist = b.district || b.distrito || null;
  const prov = b.province || b.provincia || null;
  const city = b.city || b.cityName || b.locationCity || null;

  // Prefer your Panamá-4-level style if exists, else city/address.
  const parts = [zone, correg, dist, prov].filter(Boolean);
  if (parts.length > 0) return parts.join(", ") + ", " + getMarket(b.countryCode).name;

  if (city) return `${city}, ${getMarket(b.countryCode).name}`;
  if (b.address) return String(b.address);
  return "";
}

/**
 * A "location suggestion" object
 * {
 *   id: string,
 *   label: string,
 *   sublabel: string,
 *   tokens: string,
 * }
 */
function makeSuggestionFromBiz(b) {
  const label = buildBizLocationLabel(b);
  const sublabel = b.province || b.provincia || b.city || b.cityName || b.locationCity || getMarket(b.countryCode).name;
  return {
    id: `bizloc:${label}`,
    label: label || "Ubicación",
    sublabel,
    tokens: normalizeText([label, sublabel].filter(Boolean).join(" ")),
  };
}

function dedupeSuggestions(list) {
  const seen = new Set();
  const out = [];
  for (const s of list) {
    const key = normalizeText(s.label);
    if (!key) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/**
 * useLocationSuggestions(db)
 * - Loads a sample of businesses and builds unique location suggestions.
 * - Filters suggestions client-side based on query text.
 */
export default function useLocationSuggestions(db, { maxBusinessesToScan = 800, maxSuggestions = 30 } = {}) {
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!db) return;
      if (loadedRef.current) return;

      setLoading(true);
      try {
        const bs = await listMarketBusinesses(db,maxBusinessesToScan);

        const sugsRaw = bs
          .map(makeSuggestionFromBiz)
          .filter((s) => s && s.label && s.label !== "Ubicación");

        const sugs = dedupeSuggestions(sugsRaw).slice(0, 500); // keep some cache
        if (mounted) {
          setAllSuggestions(sugs);
          loadedRef.current = true;
        }
      } catch (e) {
        console.warn("[useLocationSuggestions] load failed", e);
        if (mounted) setAllSuggestions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [db, maxBusinessesToScan]);

  const search = (q) => {
    const nq = normalizeText(q);
    if (!nq) return [];
    const hits = allSuggestions.filter((s) => s.tokens.includes(nq)).slice(0, maxSuggestions);
    return hits;
  };

  return { loading, allSuggestions, search };
}

export function normalizeLocationQuery(q) {
  return normalizeText(q);
}